import classNames from 'classnames'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router'
import { Button, Stack, Typography } from '@mui/material'

import { CREATE_SMART_QUIZ, GUIDED_QUIZESS_IMPORT } from '@/config'
import { HelperService } from '@/services/helper.service'
import { addEntityToKnowledgeGroup } from '@/store/knowledge/actions'
import { createQuizz } from '@/store/quizzes/actions'
import BasicModal from '@/components/UI/Dialog'

interface CreateQuizModalProps {
  show: boolean
  onHide: () => void
  createSmartQuiz?: boolean
  isRolePlayEnabled?: boolean
  selectedGroupId?: string
  selectedQuestionsFromBank?: any[]
}

type DisplayMode =
  | 'single_question'
  | 'continuous_scroll'
  | 'conversational_v2'
  | 'roleplay'
  | null

type QuestionMode =
  | 'one_by_one'
  | 'from_document'
  | 'from_question_bank'
  | 'import_from_document'
  | 'from_integrated_sources'

interface Quiz {
  step: number
  name: string | null
  description: string | null
  displayMode: DisplayMode | null
  questionMode: QuestionMode | null
  quizMode: string | null
  questionTypes: string[]
}

const CreateQuizModalTeachersToolkit: React.FC<CreateQuizModalProps> = ({
  onHide,
  show,
  createSmartQuiz,
  isRolePlayEnabled,
  selectedGroupId,
  selectedQuestionsFromBank,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const useLocatio = useLocation()
  const helperService = new HelperService()
  const query = new URLSearchParams(useLocatio.search)
  const group = query.get('group')
  const [quiz, setQuiz] = useState<Quiz>({
    step: 1,
    name: null,
    description: null,
    displayMode: isRolePlayEnabled ? 'roleplay' : null,
    questionMode: 'one_by_one',
    quizMode: null,
    questionTypes: [],
  })

  const EXERCISE_FORMATS: Array<{
    mode: DisplayMode
    text: string
    description: string
  }> = [
    {
      mode: 'single_question',
      text: t('One at a time'),
      description: t(
        'Display all questions at once or one at a time, following a traditional assessment format.',
      ),
    },
    {
      mode: 'continuous_scroll',
      text: t('All Questions at once'),
      description: t(
        'All questions would be shown in the same page all at once',
      ),
    },
    {
      mode: 'conversational_v2',
      text: t('Conversational'),
      description: t(
        'Questions will be asked in a chat-like style, creating an interactive and engaging experience.',
      ),
    },
  ]

  const EXERCISE_FORMATS_STEP_2: Array<{
    mode: QuestionMode
    text: string
    description: string
  }> = [
    {
      mode: 'from_document',
      text: t('Generate from Document'),
      description: t('Upload or select document(s) to generate quiz questions'),
    },
    {
      mode: 'from_question_bank',
      text: t('Import from Question Bank'),
      description: t('Select and edit questions from your question bank'),
    },
    {
      mode: 'import_from_document',
      text: t('Upload a Test'),
      description: t(
        'Extract quiz questions from an uploaded document (Requires certain formatting)',
      ),
    },
    {
      mode: 'from_integrated_sources',
      text: t('Import from integration source'),
      description: t('Import quiz questions from your integrated source'),
    },
  ]

  const [loading, setLoading] = useState(false)
  const [queType, setQueType] = useState([
    {
      isChecked: false,
      text: t('Multiple Choice Questions'),
      type: 'quiz_mcq',
    },
    { isChecked: false, text: t('Open-Ended Questions'), type: 'quiz_text' },
  ])
  const [groupId, setGroupId] = useState(group ? group : null)

  useEffect(() => {
    if (show) {
      setQuiz({
        step: 1,
        name: null,
        description: null,
        displayMode: null,
        questionMode: 'one_by_one',
        quizMode: createSmartQuiz === true ? 'smart_quiz' : null,
        questionTypes: [],
      })
      setQueType([
        {
          isChecked: false,
          text: t('Multiple Choice Questions'),
          type: 'quiz_mcq',
        },
        {
          isChecked: false,
          text: t('Open-Ended Questions'),
          type: 'quiz_text',
        },
      ])
      if (selectedGroupId) {
        setGroupId(selectedGroupId)
      }
      setLoading(false)
    }
  }, [show])

  useEffect(() => {
    const type = queType
      .filter((e: any) => e.isChecked === true)
      .map(e => e.type)
    setQuiz({
      ...quiz,
      questionTypes: [...type],
    })
  }, [queType])

  const nextStepHandler = async () => {
    if (quiz.step === 1) {
      if (isRolePlayEnabled && !quiz.name) {
        helperService.showToaster('Please enter role play name.')
        return
      }
      if (!isRolePlayEnabled && (!quiz.name || !quiz.displayMode)) {
        helperService.showToaster(
          !quiz.name
            ? 'Please enter quiz name.'
            : !quiz.displayMode
              ? 'Please choose quiz format'
              : '',
        )
        return
      }
    }

    if (quiz.name.length > 256) {
      helperService.showToaster(
        t(
          `${isRolePlayEnabled ? 'Role Play' : 'Quiz'} name should not exceed 256 characters.`,
        ),
      )
      return
    }

    // ROLE PLAY
    // if (quiz.step === 1 && quiz.displayMode === 'roleplay') {
    if (quiz.step === 1 && isRolePlayEnabled) {
      setLoading(true)

      const response: any = await dispatch(
        createQuizz({
          name: quiz.name,
          type: 'roleplay',
          display_mode: 'conversational_v2',
        }),
      )

      if (selectedGroupId) {
        await dispatch(
          addEntityToKnowledgeGroup(
            selectedGroupId,
            'quiz',
            [response._id],
            false,
            true,
          ),
        )
      }
      // if (quiz.shouldCreateButton) {
      //   await createQuizButton(response)
      // }

      history.push({
        pathname: `/quizzes/edit/${response._id}?type=build`,
        state: { origin: 'create' },
      })

      setLoading(false)
      return
    }

    // ENABLE THIS WHEN YOU NEED SEOND STEP
    if (
      quiz.step === 2 &&
      (!quiz.name || !quiz.displayMode || !quiz.questionMode)
    ) {
      return
    }
    const question_ids =
      (selectedQuestionsFromBank &&
        selectedQuestionsFromBank.map((q: any) => q?.rowId)) ||
      []

    if (quiz.step === 2 || (quiz.step === 1 && question_ids.length > 0)) {
      setLoading(true)
      let response: any = {}
      try {
        if (quiz.questionMode !== 'from_question_bank') {
          response = await dispatch(
            createQuizz(
              selectedQuestionsFromBank
                ? {
                    name: quiz.name,
                    description: quiz.description ? quiz.description : '',
                    display_mode: quiz.displayMode,
                    question_ids: selectedQuestionsFromBank.map(
                      (q: any) => q?.rowId,
                    ),
                  }
                : {
                    name: quiz.name,
                    description: quiz.description ? quiz.description : '',
                    display_mode: quiz.displayMode,
                  },
            ),
          )
        }

        if (groupId && quiz.questionMode !== 'from_question_bank') {
          await dispatch(
            addEntityToKnowledgeGroup(groupId, 'quiz', [response._id], true),
          )
        }

        // if (quiz.shouldCreateButton) {
        //   createQuizButton(response)
        // } else {
        setLoading(false)
        if (quiz.questionMode === 'one_by_one') {
          history.push({
            pathname: `/quizzes/edit/${response._id}?type=build`,
            state: { origin: 'create' },
          })
        } else if (quiz.questionMode === 'from_document') {
          history.push({
            pathname: CREATE_SMART_QUIZ,
            state: {
              groupId: groupId,
              questionType: quiz.questionTypes,
              quizId: response._id,
            },
            search: `?quizId=${response._id}&source=existing`,
          })
        } else if (quiz.questionMode === 'import_from_document') {
          history.push({
            pathname: '/import-quiz',
            state: { quizId: response._id },
            search: `?type=quiz&quizId=${response._id}`,
          })
        } else if (quiz.questionMode === 'from_question_bank') {
          history.push({
            pathname: `${GUIDED_QUIZESS_IMPORT}`,
            state: {
              groupId: groupId,
              name: quiz.name,
              description: quiz.description,
              displayMode: quiz.displayMode,
              createButtons: false,
            },
            search: `?groupId=${groupId}&name=${quiz.name}&
                description=${quiz.description}&displayMode=${quiz.displayMode}&createButtons=false`,
          })
        } else if (quiz.questionMode === 'from_integrated_sources') {
          history.push({
            pathname: CREATE_SMART_QUIZ,
            state: {
              groupId: groupId,
              questionType: quiz.questionTypes,
              quizId: response._id,
            },
            search: `?quizId=${response._id}&source=integrated`,
          })
        }
      } catch (error) {
        setLoading(false)
      }
    } else {
      setQuiz({
        ...quiz,
        step: quiz.step + 1,
      })
    }
  }

  return (
    <BasicModal
      open={show}
      onClose={onHide}
      sx={{ width: quiz.step === 2 ? 550 : 683, height: 'unset', p: 2 }}
      title={
        isRolePlayEnabled
          ? t('Create Role Play')
          : quiz.step === 2
            ? t('Add Questions to Quiz')
            : t('Create a Quiz')
      }
      showCloseButton
    >
      {quiz.step === 2 && (
        <>
          <div className="form-group">
            <p className="clr-grey-txt7 font-600">
              {t('How would you like to add questions into the quiz')}
            </p>
            <div className="tw-grid tw-gap-2 tw-grid-cols-2">
              {EXERCISE_FORMATS_STEP_2.map(format => (
                <div
                  key={format.mode}
                  onClick={() => {
                    setQuiz({
                      ...quiz,
                      questionMode: format.mode,
                    })
                  }}
                  className={classNames(
                    'tw-px-4 tw-py-6 tw-rounded-lg tw-border tw-min-h-40 tw-flex tw-flex-col tw-justify-center tw-items-center tw-cursor-pointer',
                    {
                      'tw-bg-primary tw-text-white':
                        quiz.questionMode === format.mode,
                    },
                  )}
                >
                  <h3>{format.text}</h3>
                  <p className="tw-mt-2 tw-text-center">{format.description}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="font-12 clr-grey-txt7 font-600 pb-3 text-center d-flex flex-wrap align-items-center justify-content-center">
            {t('or would you like to create from scratch? ')}
            <Button
              onClick={() => {
                setQuiz({
                  ...quiz,
                  questionMode: 'one_by_one',
                })
                nextStepHandler()
              }}
            >
              {t('Create from scratch')}
            </Button>
          </div>
        </>
      )}

      {quiz.step === 1 && (
        <>
          <div className="form-group">
            <label className="font-600">
              {isRolePlayEnabled ? t('Role Play Name') : t('Quiz Name')}
            </label>
            <input
              type="text"
              className="form-control radius-2 h-auto px-2 py-1"
              placeholder={
                isRolePlayEnabled
                  ? t('Enter name of role play')
                  : t('Enter name of quiz')
              }
              value={quiz.name || ''}
              onChange={(e: any) => {
                setQuiz({
                  ...quiz,
                  name: e.target.value,
                })
              }}
            />
          </div>
          <div className="form-group">
            <label className="font-600">
              {isRolePlayEnabled
                ? t('Role Play Description/Instructions (Optional)')
                : t('Quiz Description/Instructions (Optional)')}
            </label>
            {isRolePlayEnabled ? (
              <textarea
                className="form-control radius-2 x-2 py-1 tw-h-32"
                placeholder={t('Enter description of the role play')}
                value={quiz.description || ''}
                onChange={(e: any) => {
                  setQuiz({
                    ...quiz,
                    description: e.target.value,
                  })
                }}
              />
            ) : (
              <input
                className="form-control radius-2 h-auto px-2 py-1"
                placeholder={t('Enter description of the quiz')}
                value={quiz.description || ''}
                onChange={(e: any) => {
                  setQuiz({
                    ...quiz,
                    description: e.target.value,
                  })
                }}
              />
            )}
          </div>
          {!isRolePlayEnabled && (
            <div className="form-group">
              <label className="font-600">{t('Quiz Format')}</label>
              <Typography
                variant="body3"
                sx={{ fontWeight: 400, marginBottom: 2 }}
                component="p"
              >
                {t('Select how Walter would display the quiz questions.')}
              </Typography>
              <div className="tw-grid-cols-3 tw-grid tw-gap-2">
                {EXERCISE_FORMATS.map(format => (
                  <div
                    key={format.mode}
                    onClick={() => {
                      setQuiz(prev => ({
                        ...prev,
                        displayMode: format.mode,
                      }))
                    }}
                    className={classNames(
                      'tw-px-4 tw-py-6 tw-rounded-lg tw-border tw-min-h-40 tw-flex tw-flex-col tw-justify-center tw-items-center tw-cursor-pointer',
                      {
                        'tw-bg-primary tw-text-white':
                          quiz.displayMode === format.mode,
                      },
                    )}
                  >
                    <h3>{t(format.text)}</h3>
                    <p className="tw-mt-2 tw-text-center">
                      {t(format.description)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <Stack direction="row" justifyContent="flex-end">
        <Button
          variant="contained"
          loading={loading}
          loadingPosition="start"
          onClick={nextStepHandler}
          disabled={
            loading ||
            (quiz.step === 2 &&
              quiz.displayMode !== 'roleplay' &&
              quiz.questionMode === 'one_by_one')
          }
        >
          {t('Next')}
        </Button>
      </Stack>
    </BasicModal>
  )
}

export default CreateQuizModalTeachersToolkit
